import React from "react";

/**
 * Crisp HTML text overlays for the two LR Hub house ads.
 *
 * Both uploaded ad graphics have their wording baked into the PNG, which looks
 * soft/fuzzy at rail width. These overlays:
 *   1. cover the baked-in text with a rectangle matched to the artwork's own
 *      near-white paper colour (sampled from the PNG: #FDFDFD) — or the CTA
 *      button's navy (#081627),
 *   2. re-render the exact same wording as real HTML text in the same position,
 *      colour, weight and alignment.
 *
 * Everything is sized in `cqw` (percent of the card's own width) against a
 * container-query context, so the text scales with the image and can never
 * misalign or overflow at smaller rail widths. Geometry values below are
 * fractions measured directly off the source PNGs (1122x1402).
 *
 * NOTE: colours here are deliberately literal hex values sampled from the
 * artwork — they must match the image pixels, not the app theme tokens.
 */

const PAPER = "#FDFDFD";
const NAVY = "#001635";
const TEAL = "#007274";
const BUTTON_NAVY = "#081627";
const ITALIC_SLATE = "#4C5A66";

/** Wrapper that establishes the container-query context for the overlays. */
export const HubAdOverlayRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="pointer-events-none absolute inset-0 block"
    style={{ containerType: "inline-size" }}
    aria-hidden="true"
  >
    {children}
  </span>
);

type Box = { top: number; left: number; width: number; height: number };

/** Opaque patch that hides the baked-in fuzzy text underneath. */
const Cover: React.FC<Box & { color?: string; radius?: string }> = ({
  top,
  left,
  width,
  height,
  color = PAPER,
  radius,
}) => (
  <span
    className="absolute block"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: `${width}%`,
      height: `${height}%`,
      background: color,
      borderRadius: radius,
    }}
  />
);

/** Absolutely-positioned crisp text block. */
const Text: React.FC<{
  top: number;
  left: number;
  width?: number;
  size: number;
  lineHeight?: number;
  color?: string;
  weight?: number;
  italic?: boolean;
  tracking?: string;
  children: React.ReactNode;
}> = ({
  top,
  left,
  width,
  size,
  lineHeight = 1.06,
  color = NAVY,
  weight = 800,
  italic,
  tracking = "-0.02em",
  children,
}) => (
  <span
    className="absolute block"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: width ? `${width}%` : undefined,
      fontSize: `${size}cqw`,
      lineHeight,
      color,
      fontWeight: weight,
      fontStyle: italic ? "italic" : undefined,
      letterSpacing: tracking,
    }}
  >
    {children}
  </span>
);

/* ───────────────────────── Google Play ad ───────────────────────── */

/**
 * "Get / LaneRunner / on the go" + the download block.
 * The official Google Play badge at the bottom is left untouched — it is a
 * brand asset that must ship as artwork, not re-typed text.
 */
export const HubGooglePlayAdOverlay: React.FC = () => (
  <HubAdOverlayRoot>
    {/* headline + teal rule */}
    <Cover top={12.0} left={7} width={47} height={24.8} />
    <Text top={12.4} left={8.9} size={7.3} lineHeight={1.02}>
      Get
      <br />
      <span style={{ color: TEAL }}>LaneRunner</span>
      <br />
      on the go
    </Text>
    <span
      className="absolute block"
      style={{ top: "36.8%", left: "8.8%", width: "10%", height: "0.5%", background: TEAL }}
    />

    {/* download block (icon circle on the left is artwork and stays) */}
    <Cover top={39.2} left={18.5} width={30} height={11.4} />
    <Text top={39.9} left={20.7} size={3.1} lineHeight={1.13}>
      Download the
      <br />
      app on
      <br />
      Google Play
    </Text>

    {/* italic "coming soon" note */}
    <Cover top={51.0} left={18.5} width={30} height={6.6} />
    <Text top={51.6} left={20.7} size={2.4} lineHeight={1.35} weight={500} italic color={ITALIC_SLATE}>
      Apple App Store
      <br />
      coming soon
    </Text>
  </HubAdOverlayRoot>
);

/* ───────────────────────── Load Board ad ───────────────────────── */

/**
 * "Post to the / LaneRunner / Load Board", the three bullet lines and the
 * "Start Posting on LaneRunner" button label. The bullet icon circles, the
 * PRIVATE/LANERUNNER toggle chip and the button's send icon are artwork and
 * are left in place.
 */
export const HubLoadBoardAdOverlay: React.FC = () => (
  <HubAdOverlayRoot>
    {/* headline + teal rule */}
    <Cover top={10.5} left={5.5} width={46.5} height={25.4} />
    <Text top={11.0} left={6.6} size={7.4} lineHeight={0.99}>
      Post to the
      <br />
      <span style={{ color: TEAL }}>LaneRunner</span>
      <br />
      Load Board
    </Text>
    <span
      className="absolute block"
      style={{ top: "34.6%", left: "6.8%", width: "8.8%", height: "0.45%", background: TEAL }}
    />

    {/* bullet 1 */}
    <Cover top={38.0} left={15.5} width={34.5} height={11.2} />
    <Text top={38.6} left={17.2} size={3.4} lineHeight={1.15} weight={700}>
      Fast. Powerful.
      <br />
      Packed with
      <br />
      great features.
    </Text>

    {/* bullet 2 */}
    <Cover top={50.0} left={15.5} width={34.5} height={5.0} />
    <Text top={50.6} left={17.2} size={3.4} lineHeight={1.15} weight={700}>
      Growing daily.
    </Text>

    {/* bullet 3 */}
    <Cover top={55.8} left={15.5} width={34.5} height={14.7} />
    <Text top={56.4} left={17.2} size={3.4} lineHeight={1.15} weight={700}>
      Just click the
      <br />
      LaneRunner toggle
      <br />
      at the top of
      <br />
      the software.
    </Text>

    {/* CTA button label (send icon on the left stays) */}
    <Cover top={84.0} left={32.0} width={46.5} height={5.8} color={BUTTON_NAVY} />
    <Text
      top={85.3}
      left={32.0}
      width={46.5}
      size={3.0}
      lineHeight={1.2}
      weight={700}
      color="#FFFFFF"
      tracking="-0.01em"
    >
      <span className="block text-center">Start Posting on LaneRunner</span>
    </Text>
  </HubAdOverlayRoot>
);
